import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type {
  AccountingLocation,
  AccountingTargets,
  AccountingTeam,
  AccountingToday,
  AccountingYear,
  AccountingYearTotal,
  ExpenseCategory,
  ExpenseRow,
  SalaryPerson,
  SaveExpense,
  SaveSalary,
} from "@dentacore/shared-types";

import {
  createExpense,
  createExpenseCategory,
  deleteExpense,
  getAccountingTargets,
  getAccountingTeam,
  getAccountingToday,
  getAccountingYear,
  getAccountingYears,
  getAccountingDay,
  listAccountingLocations,
  listExpenseCategories,
  listExpenses,
  listSalaries,
  removeExpenseCategory,
  renameExpenseCategory,
  saveAccountingTargets,
  saveSalary,
  setLastDay,
  updateExpense,
} from "../api/accounting";
import { useAuth } from "../auth/auth-context";
import { useCan } from "../auth/use-can";
import { useRingProvider } from "../shell/ring/ring-registry";
import { RING_ICONS } from "../shell/ring/ring-icons";
import { Toast } from "../settings/toast";
import { describe, useWriter } from "../settings/use-writer";
import { BreakdownTab } from "./breakdown-tab";
import { CategoryDialog } from "./category-dialog";
import type { CategoryChanges } from "./category-dialog";
import { withYear, ZoomSelect } from "./controls";
import { DailyTab } from "./daily-tab";
import { ExpenseDialog } from "./expense-dialog";
import { readZoom, writeZoom } from "./zoom";
import { ExpensesTab } from "./expenses-tab";
import type { ExpensePeriod } from "./expenses-tab";
import { money, niceDay, parseDay, periodRange } from "./format";
import { formatAmounts, formatMoney } from "../patients/money";
import { MonthlyTab } from "./monthly-tab";
import { SalariesTab } from "./salaries-tab";
import { TeamTab } from "./team-tab";
import { TargetsDialog } from "./targets-dialog";
import { YearlyTab } from "./yearly-tab";
import styles from "./accounting.module.css";
import { FitSections } from "./fit-sections";

type Tab = "daily" | "yearly" | "monthly" | "team" | "breakdown" | "expenses" | "salaries";

/** Each tab's name as its button says it — the second line of the page's ring. */
const TAB_LABELS: Record<Tab, string> = {
  daily: "Daily",
  yearly: "Yearly",
  monthly: "Monthly",
  team: "Team",
  breakdown: "Breakdown",
  expenses: "Expenses",
  salaries: "Salaries",
};

type Dialog =
  | { kind: "expense"; expense: ExpenseRow | null; deleting?: boolean }
  | { kind: "categories" }
  | { kind: "targets" };

/**
 * Accounting — **profit by year, month and day, everything paid and received,
 * expenses and salaries** (STORY-172, decisions AC-D1–AC-D22).
 *
 * Laid out as the approved prototype is: a header with the location chips and
 * *Add expense*, then five tabs — Yearly, Monthly, Breakdown, Expenses,
 * Salaries — like Inventory's. Yearly and Monthly replaced Overview
 * (STORY-180b, ER-D8), each showing Profit beside Expected and Realized
 * revenue (ER-D1).
 *
 * ── Every number comes from the API ──────────────────────────────────────
 *
 * The money rules live once, in `@dentacore/shared-types/accounting`, and the
 * server applies them: money in is payments on the day received (AC-D1),
 * materials are supplier orders on the day received (AC-D2), a day leaves out
 * salaries and expenses (AC-D5, AC-D8), a salary is counted for the days
 * employed and up to today in the current month (AC-D11, AC-D17). **This
 * screen adds months together and nothing more**, so it cannot disagree with
 * the server about what a figure is.
 *
 * ── Today is the clinic's ────────────────────────────────────────────────
 *
 * "So far", the current month, the latest date an expense may be paid — all
 * come from `AccountingYear.today`, the clinic's own today. The browser's
 * clock is read exactly once, to choose which year to ask for first; if the
 * clinic's today turns out to be in another year (New Year's Eve across a
 * timezone), the page moves to it.
 *
 * ── Locations ────────────────────────────────────────────────────────────
 *
 * Any combination of the locations the caller may read (AC-D6), at least one,
 * all by default. A salary or a shared expense counts only its share in a
 * partial selection (AC-D7, AC-D9) — the server works that out; the screen
 * says when a figure is a share.
 *
 * ── Who sees it ──────────────────────────────────────────────────────────
 *
 * Five rights since QA4-D8 (2026-10-18): **See profit** (`accounting.view`)
 * draws Daily, Yearly, Monthly, Team and Breakdown; **See expenses** the
 * Expenses tab; **See everyone's salary** the Salaries tab; **Record
 * expenses** (`accounting.manage`) and **Set salaries** add the writes. Any one
 * of the three *See* opens the page, on the first tab it may show. All of it is
 * presentation here — the API refuses on its own, and strips each person's pay
 * and each category's spending from the profit figures for whoever may not read
 * them.
 */
export function AccountingPage(): React.JSX.Element {
  const can = useCan();
  const seesProfit = can("accounting.view");
  const seesExpenses = can("accounting.expenses.view");
  const seesSalaries = can("accounting.salaries.view");
  const canManage = can("accounting.manage");
  const canSetPay = can("accounting.salaries.manage");
  /** Which part the location chips are for: the first one this person may read. */
  const part = seesProfit ? "profit" : seesExpenses ? "expenses" : "salaries";
  const { user } = useAuth();
  /** Salaries and shared expenses are in it (QA4-D6); every location has its own. */
  const home = user?.currency ?? "USD";
  const { busy, toast, dismiss, fail, run } = useWriter();

  // Opens on today (DY-D4): the first thing a front desk asks is what is coming in.
  // …or, without profit, on the first part this person may read (QA4-D8).
  const [tab, setTab] = useState<Tab>(() =>
    seesProfit ? "daily" : seesExpenses ? "expenses" : "salaries",
  );
  // How large the screen is drawn: this browser's own choice (STORY-186).
  const [zoom, setZoom] = useState(readZoom);
  const [dialog, setDialog] = useState<Dialog | null>(null);
  // Remounts the categories dialog from a fresh list after a save that half landed.
  const [categoryDialogKey, setCategoryDialogKey] = useState(0);

  const [locations, setLocations] = useState<readonly AccountingLocation[] | null>(null);
  /** Selected location ids, always in the order the locations are listed. */
  const [selected, setSelected] = useState<readonly string[]>([]);

  // The browser's year is only the first guess — see "Today is the clinic's".
  const [year, setYear] = useState(() => new Date().getFullYear());
  /** The month Monthly shows. Null until somebody picks one — see `shownMonth`. */
  const [month, setMonth] = useState<number | null>(null);
  const settledYear = useRef(false);
  const [today, setToday] = useState<string | null>(null);
  const [yearData, setYearData] = useState<AccountingYear | null>(null);
  const [years, setYears] = useState<readonly AccountingYearTotal[] | null>(null);
  const [todayData, setTodayData] = useState<AccountingToday | null>(null);
  /** The month Team shows; 0 is the whole year. Null until chosen: the clinic's current month. */
  const [teamMonth, setTeamMonth] = useState<number | null>(null);
  const [teamData, setTeamData] = useState<AccountingTeam | null>(null);

  /** The clinic's today as year, month, day — null until it is known. */
  const thisYear = today === null ? null : parseDay(today);

  // Team opens on the clinic's current month in its current year, and on December of a past
  // year, the whole of a year still ahead being empty.
  const shownTeamMonth =
    teamMonth ??
    (thisYear === null ? 0 : year > thisYear.year ? 0 : year < thisYear.year ? 12 : thisYear.month);

  const [categories, setCategories] = useState<readonly ExpenseCategory[] | null>(null);
  /** The clinic's targets (AF-D6). Null until read; nothing is judged against them meanwhile. */
  const [targets, setTargets] = useState<AccountingTargets | null>(null);
  const [salaries, setSalaries] = useState<readonly SalaryPerson[] | null>(null);
  const [expensePeriod, setExpensePeriod] = useState<ExpensePeriod | null>(null);
  const [expenses, setExpenses] = useState<readonly ExpenseRow[] | null>(null);
  /** Moves after every write, so a tab that reads for itself reads again. */
  const [version, setVersion] = useState(0);

  const branches = selected.length === 0 ? null : selected.join(",");

  /* ── loading ──────────────────────────────────────────────────────────── */

  const loadYear = useCallback(
    async (signal?: AbortSignal) => {
      if (branches === null || !seesProfit) return;
      const data = await getAccountingYear(year, branches, signal);
      setYearData(data);
      setToday(data.today);
    },
    [year, branches, seesProfit],
  );

  const loadToday = useCallback(
    async (signal?: AbortSignal) => {
      if (branches === null || !seesProfit) return;
      setTodayData(await getAccountingToday(branches, signal));
    },
    [branches, seesProfit],
  );

  const loadTeam = useCallback(
    async (month: number, signal?: AbortSignal) => {
      if (branches === null) return;
      setTeamData(await getAccountingTeam(year, month, branches, signal));
    },
    [year, branches],
  );

  const loadYears = useCallback(
    async (signal?: AbortSignal) => {
      if (branches === null || !seesProfit) return;
      setYears((await getAccountingYears(branches, signal)).years);
    },
    [branches, seesProfit],
  );

  const loadExpenses = useCallback(
    async (signal?: AbortSignal) => {
      if (branches === null || expensePeriod === null || !seesExpenses) return;
      const { from, to } = periodRange(expensePeriod.year, expensePeriod.month);
      setExpenses(await listExpenses(from, to, branches, signal));
    },
    [branches, expensePeriod, seesExpenses],
  );

  const loadCategories = useCallback(
    async (signal?: AbortSignal) => {
      if (!seesExpenses) return;
      setCategories(await listExpenseCategories(signal));
    },
    [seesExpenses],
  );

  const loadSalaries = useCallback(
    async (signal?: AbortSignal) => {
      if (!seesSalaries) return;
      setSalaries(await listSalaries(signal));
    },
    [seesSalaries],
  );

  const loadTargets = useCallback(
    async (signal?: AbortSignal) => {
      if (!seesProfit) return;
      setTargets(await getAccountingTargets(signal));
    },
    [seesProfit],
  );

  /** Everything a write can move: totals, the year list, expenses, categories, salaries. */
  const reloadAll = useCallback(async () => {
    await Promise.all([
      loadYear(),
      loadToday(),
      loadYears(),
      loadExpenses(),
      loadCategories(),
      loadSalaries(),
      loadTargets(),
    ]);
    setVersion((was) => was + 1);
  }, [loadYear, loadToday, loadYears, loadExpenses, loadCategories, loadSalaries, loadTargets]);

  // The locations, the categories and the people: read once.
  useEffect(() => {
    const controller = new AbortController();
    const failed =
      (message: string, clear: () => void) =>
      (cause: unknown): void => {
        if (controller.signal.aborted) return;
        clear();
        fail(describe(cause, message));
      };

    // Without profit there are no year figures to learn today from (QA4-D8).
    if (!seesProfit) {
      getAccountingDay(controller.signal)
        .then(({ today: day }) => setToday(day))
        .catch(failed("Today's date could not be read.", () => undefined));
    }
    listAccountingLocations(controller.signal, part)
      .then((rows) => {
        setLocations(rows);
        // **One currency at a time** (QA4-D6): the locations in the home
        // currency, or — with none there — those in the first one's.
        const start = rows.some((row) => row.currency === home) ? home : rows[0]?.currency;
        setSelected(rows.filter((row) => row.currency === start).map((row) => row.id));
      })
      .catch(failed("The locations could not be read.", () => setLocations([])));
    loadCategories(controller.signal).catch(
      failed("The expense categories could not be read.", () => setCategories([])),
    );
    loadSalaries(controller.signal).catch(
      failed("The salaries could not be read.", () => setSalaries([])),
    );
    // Without targets nothing is coloured, which is also what a clinic with none sees.
    loadTargets(controller.signal).catch(
      failed("The targets could not be read.", () => setTargets(null)),
    );
    return () => controller.abort();
  }, [loadCategories, loadSalaries, loadTargets, fail, seesProfit, part, home]);

  // The year, again whenever the year or the selection changes.
  useEffect(() => {
    if (branches === null) return;
    const controller = new AbortController();
    loadYear(controller.signal).catch((cause: unknown) => {
      if (controller.signal.aborted) return;
      // Never leave the last selection's figures beside the new selection's chips.
      setYearData(null);
      fail(describe(cause, "The accounts could not be read."));
    });
    return () => controller.abort();
  }, [loadYear, branches, fail]);

  // Team, when its tab is open and whenever the year, month or selection changes.
  // Cleared first, so another period's figures are never drawn under this one.
  useEffect(() => {
    if (tab !== "team" || branches === null) return;
    const controller = new AbortController();
    setTeamData(null);
    loadTeam(shownTeamMonth, controller.signal).catch((cause: unknown) => {
      if (controller.signal.aborted) return;
      fail(describe(cause, "The team figures could not be read."));
    });
    return () => controller.abort();
  }, [tab, loadTeam, shownTeamMonth, branches, fail]);

  // Today, again whenever the selection changes. Cleared first, so another
  // selection's figures are never drawn under these chips.
  useEffect(() => {
    if (branches === null) return;
    const controller = new AbortController();
    setTodayData(null);
    loadToday(controller.signal).catch((cause: unknown) => {
      if (controller.signal.aborted) return;
      fail(describe(cause, "Today's figures could not be read."));
    });
    return () => controller.abort();
  }, [loadToday, branches, fail]);

  useEffect(() => {
    if (branches === null) return;
    const controller = new AbortController();
    loadYears(controller.signal).catch((cause: unknown) => {
      if (controller.signal.aborted) return;
      setYears([]);
      fail(describe(cause, "The years could not be read."));
    });
    return () => controller.abort();
  }, [loadYears, branches, fail]);

  useEffect(() => {
    if (branches === null || expensePeriod === null) return;
    const controller = new AbortController();
    setExpenses(null);
    loadExpenses(controller.signal).catch((cause: unknown) => {
      if (controller.signal.aborted) return;
      setExpenses([]);
      fail(describe(cause, "The expenses could not be read."));
    });
    return () => controller.abort();
  }, [loadExpenses, branches, expensePeriod, fail]);

  // Once the clinic's today is known: move to its year if the guess was wrong,
  // and open the Expenses tab on its month.
  useEffect(() => {
    if (today === null || settledYear.current) return;
    settledYear.current = true;
    const { year: thisYear, month } = parseDay(today);
    setYear(thisYear);
    setExpensePeriod({ year: thisYear, month });
  }, [today]);

  /* ── derived ──────────────────────────────────────────────────────────── */

  const all = locations !== null && locations.length > 0 && selected.length === locations.length;
  // The locations on the chips, in the order they are listed — the left of the money flow.
  const selectedLocations = (locations ?? []).filter((row) => selected.includes(row.id));
  /** Every figure is in the selection's currency — the year's word on it once read. */
  const currency = shownYearCurrency(yearData, year) ?? selectedLocations[0]?.currency ?? home;
  /** The locations of the selection's currency — what *All* selects. */
  const sameCurrency = (locations ?? []).filter((row) => row.currency === currency);
  /** Shown on each chip only when the clinic's locations are in more than one. */
  const mixed = new Set((locations ?? []).map((row) => row.currency)).size > 1;
  const selectionLabel =
    locations === null || all
      ? "all locations"
      : locations
          .filter((row) => selected.includes(row.id))
          .map((row) => row.name)
          .join(" + ");

  /** Every year holding money, plus the clinic's current one and the one on screen. */
  const yearOptions = useMemo(() => {
    let options = (years ?? []).map((row) => row.year);
    if (today !== null) options = withYear(options, parseDay(today).year);
    return withYear(options, year);
  }, [years, today, year]);

  // Figures for another year must not be drawn under this year's heading.
  const shownYear = yearData !== null && yearData.year === year ? yearData : null;

  // Until a month is picked: today's in the current year, December in a past
  // one, January in a year still ahead.
  const shownMonth =
    month ??
    (thisYear === null || year > thisYear.year ? 1 : year < thisYear.year ? 12 : thisYear.month);

  const stillHere =
    salaries === null || today === null
      ? null
      : salaries.filter((row) => row.endedOn === null || row.endedOn >= today).length;

  const toggleLocation = (id: string): void =>
    setSelected((was) => {
      // At least one location stays selected.
      if (was.includes(id)) return was.length > 1 ? was.filter((row) => row !== id) : was;
      // A location in another currency starts a selection of its own: amounts in
      // two currencies are never added together (QA4-D6).
      const picked = (locations ?? []).find((row) => row.id === id);
      const current = (locations ?? []).find((row) => row.id === was[0]);
      if (picked !== undefined && current !== undefined && picked.currency !== current.currency) {
        return [id];
      }
      const next = new Set([...was, id]);
      return (locations ?? []).map((row) => row.id).filter((row) => next.has(row));
    });

  /* ── writes ───────────────────────────────────────────────────────────── */

  const saveCategories = async (changes: CategoryChanges): Promise<boolean> => {
    let landed = 0;
    const ok = await run(
      async () => {
        // Removals first, so a name freed by one can be reused by another.
        for (const category of changes.remove) {
          await removeExpenseCategory(category.id);
          landed++;
        }
        for (const rename of changes.rename) {
          await renameExpenseCategory(rename.id, rename.name, rename.group);
          landed++;
        }
        for (const added of changes.add) {
          await createExpenseCategory(added.name, added.group);
          landed++;
        }
      },
      reloadAll,
      "Categories saved.",
    );
    if (!ok && landed > 0) {
      // Some of it is saved. Reopen the list from what the server now holds, so
      // pressing Save again does not send those changes a second time.
      await loadCategories().catch(() => undefined);
      setCategoryDialogKey((was) => was + 1);
    }
    return ok;
  };

  /* ── the quick ring (QR7–QR9, STORY-221) ──────────────────────────────── */

  // Each action opens the dialog the page's own button opens, on the same
  // conditions that button is drawn and enabled on (QR8). Writes need the
  // Accounting add-on switched on: switched off it stays readable (MOD-D).
  const canAddExpense = !busy && today !== null && locations !== null;

  // Blank space: the header's *Add expense* and *Targets*, and the Expenses tab's *Categories*.
  useRingProvider("page", () => ({
    ring: "page.accounting",
    title: "Accounting",
    meta: TAB_LABELS[tab],
    actions: [
      canAddExpense && {
        key: "exp",
        label: "Add expense",
        icon: RING_ICONS.plus,
        needs: ["accounting.manage"],
        module: "ACCOUNTING",
        hint: "Rent, lab fees, supplies",
        run: () => setDialog({ kind: "expense", expense: null }),
      },
      !busy &&
        targets !== null && {
          key: "targets",
          label: "Targets",
          icon: RING_ICONS.target,
          needs: ["accounting.manage", "accounting.view"],
          module: "ACCOUNTING",
          hint: "Monthly targets",
          run: () => setDialog({ kind: "targets" }),
        },
      categories !== null && {
        key: "cats",
        label: "Categories",
        icon: RING_ICONS.tag,
        needs: ["accounting.manage"],
        module: "ACCOUNTING",
        hint: "Expense categories",
        run: () => setDialog({ kind: "categories" }),
      },
    ],
  }));

  // One expense in the Expenses tab: its *Edit*, and the *Delete* inside that dialog.
  useRingProvider("acc.expense", (element) => {
    const expense = (expenses ?? []).find((row) => row.id === element.dataset.ringId);
    if (expense === undefined) return null;
    const category =
      (categories ?? []).find((row) => row.id === expense.categoryId)?.name ?? "Expense";
    const open = today !== null;
    return {
      ring: "acc.expense",
      title: expense.note !== null && expense.note !== "" ? expense.note : category,
      meta: `${niceDay(expense.paidOn)} · ${money(expense.amountCents, expense.currency)}`,
      subject: { type: "expense", id: expense.id },
      actions: [
        open && {
          key: "edit",
          label: "Edit",
          icon: RING_ICONS.pencil,
          needs: ["accounting.manage"],
          module: "ACCOUNTING",
          hint: "Amount, date, category",
          run: () => setDialog({ kind: "expense", expense }),
        },
        open &&
          !busy && {
            key: "del",
            label: "Delete",
            icon: RING_ICONS.retire,
            needs: ["accounting.manage"],
            module: "ACCOUNTING",
            danger: true,
            hint: "Asks first",
            run: () => setDialog({ kind: "expense", expense, deleting: true }),
          },
      ],
    };
  });

  /* ── render ───────────────────────────────────────────────────────────── */

  const tabButton = (key: Tab, label: string, count?: number | null): React.JSX.Element => (
    <button
      type="button"
      role="tab"
      aria-selected={tab === key}
      className={tab === key ? `${styles.tab} ${styles.tabOn}` : styles.tab}
      onClick={() => setTab(key)}
      data-testid={`accounting-tab-${key}`}
    >
      {label}
      {count !== undefined && count !== null && <span className={styles.tabCount}>{count}</span>}
    </button>
  );

  return (
    <div className={styles.page} data-testid="accounting-page">
      <header className={styles.head}>
        <h1 className={styles.title}>Accounting</h1>
        <div className={styles.headActions}>
          <ZoomSelect
            value={zoom}
            onChange={(next) => {
              setZoom(next);
              writeZoom(next);
            }}
          />
          {locations !== null && locations.length > 0 && (
            <div className={styles.locations} role="group" aria-label="Locations">
              <span className={styles.locationsLabel}>Locations</span>
              {locations.map((row) => {
                const on = selected.includes(row.id);
                return (
                  <button
                    key={row.id}
                    type="button"
                    className={
                      on ? `${styles.chipButton} ${styles.chipButtonOn}` : styles.chipButton
                    }
                    aria-pressed={on}
                    onClick={() => toggleLocation(row.id)}
                    data-testid={`accounting-loc-${row.id}`}
                  >
                    <span className={styles.tick} aria-hidden="true">
                      {on ? "✓" : ""}
                    </span>
                    {row.name}
                    {mixed && <span className={styles.note}>&nbsp;{row.currency}</span>}
                  </button>
                );
              })}
              <button
                type="button"
                className={`${styles.quiet} ${styles.small}`}
                disabled={all || selected.length === sameCurrency.length}
                onClick={() => setSelected(sameCurrency.map((row) => row.id))}
                data-testid="accounting-loc-all"
              >
                All
              </button>
            </div>
          )}
          {canManage && seesProfit && (
            <button
              type="button"
              className={styles.ghost}
              disabled={busy || targets === null}
              onClick={() => setDialog({ kind: "targets" })}
              data-testid="accounting-targets"
            >
              Targets
            </button>
          )}
          {canManage && (
            <button
              type="button"
              className={styles.primary}
              disabled={busy || today === null || locations === null}
              onClick={() => setDialog({ kind: "expense", expense: null })}
              data-testid="accounting-add-expense"
            >
              Add expense
            </button>
          )}
        </div>
      </header>

      <div className={styles.zoomed} style={{ zoom: zoom / 100 }} data-testid="accounting-zoomed">
        <div
          className={styles.tabs}
          role="tablist"
          aria-label="Daily, yearly, monthly, team, breakdown, expenses or salaries"
        >
          {seesProfit && tabButton("daily", "Daily")}
          {seesProfit && tabButton("yearly", "Yearly")}
          {seesProfit && tabButton("monthly", "Monthly")}
          {seesProfit && tabButton("team", "Team")}
          {seesProfit && tabButton("breakdown", "Breakdown")}
          {seesExpenses && tabButton("expenses", "Expenses", expenses?.length ?? null)}
          {seesSalaries && tabButton("salaries", "Salaries", stillHere)}
        </div>

        <CurrencyNotes year={shownYearCurrency(yearData, year) === null ? null : yearData} />

        {/* A page of whole parts at a time, fitted to the window (FitSections). */}
        <FitSections resetOn={`${tab}|${String(year)}|${String(shownMonth)}`}>
          {locations !== null && locations.length === 0 ? (
            <div className={styles.card}>
              <p className={styles.blank}>There are no locations whose accounts you can see.</p>
            </div>
          ) : tab === "daily" ? (
            <DailyTab data={todayData} currency={currency} selectionLabel={selectionLabel} />
          ) : tab === "yearly" ? (
            <YearlyTab
              data={shownYear}
              years={years}
              year={year}
              yearOptions={yearOptions}
              onYear={setYear}
              onMonth={(picked) => {
                setMonth(picked);
                setTab("monthly");
              }}
              currency={currency}
              selectionLabel={selectionLabel}
              targets={targets}
              locations={selectedLocations}
            />
          ) : tab === "monthly" ? (
            <MonthlyTab
              data={shownYear}
              year={year}
              month={shownMonth}
              canGoBack={!(shownMonth === 1 && year <= (yearOptions[0] ?? year))}
              canGoForward={!(shownMonth === 12 && year >= (yearOptions.at(-1) ?? year))}
              onMonth={(next) => {
                // ‹ in January and › in December cross into the neighbouring year.
                if (next === 0) {
                  setMonth(12);
                  setYear(year - 1);
                } else if (next === 13) {
                  setMonth(1);
                  setYear(year + 1);
                } else {
                  setMonth(next);
                }
              }}
              currency={currency}
              selectionLabel={selectionLabel}
              targets={targets}
              locations={selectedLocations}
            />
          ) : tab === "team" ? (
            today === null ? (
              <div className={styles.card}>
                <p className={styles.blank}>Loading…</p>
              </div>
            ) : (
              <TeamTab
                data={teamData}
                year={year}
                month={shownTeamMonth}
                yearOptions={yearOptions}
                today={today}
                onYear={setYear}
                onMonth={setTeamMonth}
                currency={currency}
                selectionLabel={selectionLabel}
                showLocation={selected.length > 1}
              />
            )
          ) : tab === "breakdown" ? (
            branches === null ? (
              <div className={styles.card}>
                <p className={styles.blank}>Loading…</p>
              </div>
            ) : (
              <BreakdownTab
                data={shownYear}
                year={year}
                yearOptions={yearOptions}
                onYear={setYear}
                branches={branches}
                locations={locations ?? []}
                selected={selected}
                categories={categories}
                salaries={salaries}
                seesPay={seesSalaries}
                seesSpending={seesExpenses}
                currency={currency}
                selectionLabel={selectionLabel}
                version={version}
                fail={fail}
              />
            )
          ) : tab === "expenses" ? (
            expensePeriod === null || today === null ? (
              <div className={styles.card}>
                <p className={styles.blank}>Loading…</p>
              </div>
            ) : (
              <ExpensesTab
                period={expensePeriod}
                onPeriod={setExpensePeriod}
                yearOptions={withYear(yearOptions, expensePeriod.year)}
                today={today}
                expenses={expenses}
                categories={categories}
                locations={locations ?? []}
                allSelected={all}
                selectionLabel={selectionLabel}
                currency={currency}
                canManage={canManage}
                onEdit={(expense) => setDialog({ kind: "expense", expense })}
                onCategories={() => setDialog({ kind: "categories" })}
              />
            )
          ) : today === null ? (
            <div className={styles.card}>
              <p className={styles.blank}>Loading…</p>
            </div>
          ) : (
            <SalariesTab
              people={salaries}
              today={today}
              locations={locations ?? []}
              currency={home}
              canManage={canSetPay}
              busy={busy}
              onSaveSalary={(person: SalaryPerson, body: SaveSalary) =>
                run(
                  () => saveSalary(person.userId, body),
                  reloadAll,
                  `${person.name}'s salary is saved, from ${niceDay(body.effectiveFrom)}.`,
                )
              }
              onSetLastDay={(person, endedOn) =>
                run(
                  () => setLastDay(person.userId, { endedOn }),
                  reloadAll,
                  endedOn === null
                    ? `${person.name}'s last day is removed.`
                    : `${person.name}'s last day is ${niceDay(endedOn)}.`,
                )
              }
            />
          )}
        </FitSections>
      </div>

      {dialog?.kind === "expense" && today !== null && (
        <ExpenseDialog
          key={`${dialog.expense?.id ?? "new"}${dialog.deleting === true ? "|delete" : ""}`}
          expense={dialog.expense}
          startOnDelete={dialog.deleting === true}
          categories={categories ?? []}
          locations={locations ?? []}
          defaultBranchId={selected[0] ?? null}
          today={today}
          homeCurrency={home}
          busy={busy}
          onClose={() => setDialog(null)}
          onCreate={(body: SaveExpense) =>
            run(() => createExpense(body), reloadAll, "Expense added.")
          }
          onUpdate={(changes) => {
            const id = dialog.expense?.id;
            if (id === undefined) return Promise.resolve(false);
            return run(() => updateExpense(id, changes), reloadAll, "Expense saved.");
          }}
          onDelete={() => {
            const id = dialog.expense?.id;
            if (id === undefined) return Promise.resolve(false);
            return run(() => deleteExpense(id), reloadAll, "Expense deleted.");
          }}
        />
      )}

      {dialog?.kind === "categories" && categories !== null && (
        <CategoryDialog
          key={categoryDialogKey}
          categories={categories}
          busy={busy}
          onSave={saveCategories}
          onClose={() => setDialog(null)}
        />
      )}

      {dialog?.kind === "targets" && targets !== null && (
        <TargetsDialog
          targets={targets}
          busy={busy}
          onSave={(next) =>
            run(
              async () => {
                setTargets(await saveAccountingTargets(next));
              },
              async () => undefined,
              "Targets saved.",
            )
          }
          onClose={() => setDialog(null)}
        />
      )}

      {toast !== null && <Toast kind={toast.kind} message={toast.message} onDismiss={dismiss} />}
    </div>
  );
}

/** The year's currency, once the figures on screen are this year's. */
function shownYearCurrency(data: AccountingYear | null, year: number): string | null {
  return data !== null && data.year === year ? data.currency : null;
}

/**
 * **What the figures leave out, said beside them** (QA4-D6, QA4-D14). Away
 * from the home currency, salaries, shared expenses and supplier purchases are
 * in the home currency and so are not taken off the profit; money received in
 * another currency — on invoices from before a location changed — is not
 * counted at all. Neither is converted, and neither is lost.
 */
function CurrencyNotes({ year }: { year: AccountingYear | null }): React.JSX.Element | null {
  if (year === null) return null;
  const aside = year.months.reduce(
    (sum, m) =>
      sum +
      (m.notTakenOff === null
        ? 0
        : m.notTakenOff.salaries + m.notTakenOff.materials + m.notTakenOff.expenses),
    0,
  );
  const away = year.currency !== year.homeCurrency;
  if (!away && year.notCounted.length === 0) return null;
  return (
    <div className={styles.note} data-testid="accounting-currency-notes">
      {away && (
        <p className={styles.note} data-testid="accounting-not-taken-off">
          Figures are in {year.currency}. Salaries, shared expenses and supplier purchases are in{" "}
          {year.homeCurrency} and are not taken off the profit:{" "}
          {formatMoney(aside, year.homeCurrency)} this year.
        </p>
      )}
      {year.notCounted.length > 0 && (
        <p className={styles.note} data-testid="accounting-not-counted">
          Also received {formatAmounts(year.notCounted, year.currency)}, on invoices issued before a
          location changed its currency — not counted in these figures.
        </p>
      )}
    </div>
  );
}
